import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { setRedirectIntent } from "@/lib/redirect-intent";
import { isFreePrice, registrationMode } from "@/lib/registration-mode";
import { isPastEvent } from "@/lib/events-calendar";
import { appendCodeToWhatsappMessage, setPendingRsvp } from "@/lib/pending-rsvp";
import { AegisBadge } from "@/components/AegisBadge";
import { MrDriveAvatar } from "@/components/MrDriveAvatar";
import { SquareCover } from "@/components/SquareCover";
import { GuestInviteBlock } from "@/components/GuestInviteBlock";
import { Button } from "@/components/ui/button";
import { MapPin } from "lucide-react";

const SITE = "https://turkey-drive.com";
export const INVITE_REF_KEY = "invite_ref";

export type PublicEvent = {
  id: string;
  title: string;
  description: string | null;
  cover_image_url: string | null;
  event_date: string | null;
  event_end_date: string | null;
  city: string | null;
  price: string | null;
  ticket_url: string | null;
  organizer_user_id: string | null;
  organizer_name: string | null;
  organizer_photo_url: string | null;
  co_organizer_user_id: string | null;
  co_organizer_name: string | null;
  co_organizer_photo_url: string | null;
  attendees_count: number;
  signup_via_organizer?: boolean | null;
  location_details?: string | null;
};

async function loadPublicEvent(eventId: string): Promise<PublicEvent | null> {
  const { data, error } = await supabase.rpc("public_event", { event_id: eventId });
  if (error) return null;
  const rows = (data ?? []) as PublicEvent[];
  return rows[0] ?? null;
}

const TZ = "Europe/Istanbul";

/** Часы и минуты события по Стамбулу (UTC+3 круглый год). */
function istanbulParts(iso: string): { hm: string; ymd: string; isoLocal: string } {
  const d = new Date(new Date(iso).getTime() + 3 * 3600_000);
  const isoLocal = d.toISOString().slice(0, 19);
  return { hm: isoLocal.slice(11, 16), ymd: isoLocal.slice(0, 10), isoLocal };
}

function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long", timeZone: TZ });
}

function formatDate(iso: string | null): string {
  if (!iso) return "Дата уточняется";
  if (istanbulParts(iso).hm === "00:00") return `${dayLabel(iso)} · Время уточняется`;
  return new Date(iso).toLocaleString("ru-RU", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: TZ,
  });
}

/** Первая ссылка на карту из деталей места. */
function mapUrlFrom(details: string | null | undefined): string | null {
  if (!details) return null;
  const m = details.match(/(?:https?:\/\/|www\.|(?:maps\.)?app\.goo\.gl\/|goo\.gl\/)\S+/i);
  if (!m) return null;
  const raw = m[0].replace(/[),.;!?]+$/, "");
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

function isoForSchema(iso: string | null): string | undefined {
  if (!iso) return undefined;
  const { hm, ymd, isoLocal } = istanbulParts(iso);
  return hm === "00:00" ? ymd : `${isoLocal}+03:00`;
}

const CHAT_LINK_RE = /(wa\.me|t\.me|instagram\.com)/i;
function ticketLink(url: string | null | undefined): string | null {
  const t = url?.trim();
  return t && !CHAT_LINK_RE.test(t) ? t : null;
}

/** Текст места без ссылок и служебных частей. */
function placeText(details: string | null | undefined): string {
  if (!details) return "";
  const cleaned = details.replace(/(?:https?:\/\/|www\.|(?:maps\.)?app\.goo\.gl\/|goo\.gl\/)\S+/gi, "");
  const line = cleaned.split(/\r?\n/).map((l) => l.trim()).find((l) => l) ?? "";
  return line
    .replace(/^📍\s*/, "")
    .replace(/^[,;·\-–\s]+/, "")
    .replace(/[,;·\-–\s]+$/, "")
    .trim();
}

/** Цена указана явно (не пусто). */
function hasPrice(price: string | null): boolean {
  return !!price?.trim();
}

function priceNumber(price: string | null): number | null {
  if (!hasPrice(price)) return null;
  if (isFreePrice(price)) return 0;
  const m = (price ?? "").replace(/\s/g, "").match(/^(\d+(?:[.,]\d+)?)(?:₺|tl|try|лир[аы]?)?$/i);
  return m ? Number(m[1].replace(",", ".")) : null;
}

const LOCATION_LABEL_RE =
  /^\s*(локация|адрес|место(?:\s+встречи)?|location|address|venue)\s*[:：-]/i;

export function sanitizePublicDescription(text: string | null | undefined): string {
  if (!text) return "";
  const lines = text.split(/\r?\n/);
  const kept: string[] = [];
  for (const raw of lines) {
    if (/📍/.test(raw)) continue;
    if (LOCATION_LABEL_RE.test(raw)) continue;
    // вырезаем любые URL (включая карты)
    const stripped = raw
      .replace(/\b(?:https?:\/\/|www\.)\S+/gi, "")
      .replace(/\b[\w-]+\.(?:app\.goo\.gl|goo\.gl)\/\S*/gi, "")
      .replace(/[ \t]{2,}/g, " ")
      .trimEnd();
    kept.push(stripped);
  }
  return kept
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .split("\n")
    .filter((l, i, arr) => !(l.trim() === "" && (i === 0 || i === arr.length - 1)))
    .join("\n")
    .trim();
}

function isPaid(price: string | null): boolean {
  return !isFreePrice(price);
}


export { appendCodeToWhatsappMessage };

export const Route = createFileRoute("/e/$eventId")({
  loader: ({ params }) => loadPublicEvent(params.eventId),
  head: ({ params, loaderData }) => {
    const url = `${SITE}/e/${params.eventId}`;
    if (!loaderData) {
      return {
        meta: [{ title: "Мероприятие — TurkeyDrive" }, { name: "robots", content: "noindex" }],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const title = `${loaderData.title} — TurkeyDrive`;
    const description =
      sanitizePublicDescription(loaderData.description).slice(0, 155) ||
      `${formatDate(loaderData.event_date)} · ${loaderData.city ?? "Турция"} — встреча сообщества TurkeyDrive.`;
    const image = loaderData.cover_image_url;
    const city = !loaderData.organizer_user_id;
    const place = placeText(loaderData.location_details);
    const orgName =
      (city ? loaderData.co_organizer_name : loaderData.organizer_name)?.trim() || "TurkeyDrive";
    const num = priceNumber(loaderData.price);
    const ld: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: loaderData.title,
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      organizer: { "@type": orgName === "TurkeyDrive" ? "Organization" : "Person", name: orgName },
    };
    const ldDesc = sanitizePublicDescription(loaderData.description).slice(0, 300);
    if (ldDesc) ld.description = ldDesc;
    const start = isoForSchema(loaderData.event_date);
    if (start) ld.startDate = start;
    const end = isoForSchema(loaderData.event_end_date);
    if (end) ld.endDate = end;
    if (image) ld.image = image;
    if (loaderData.city || place) {
      ld.location = {
        "@type": "Place",
        ...(place ? { name: place } : {}),
        address: {
          "@type": "PostalAddress",
          ...(loaderData.city ? { addressLocality: loaderData.city } : {}),
          addressCountry: "TR",
        },
      };
    }
    if (num !== null) {
      ld.offers = {
        "@type": "Offer",
        price: num,
        priceCurrency: "TRY",
        url: ticketLink(loaderData.ticket_url) ?? url,
        availability: "https://schema.org/InStock",
      };
    }
    return {
      scripts: [{ type: "application/ld+json", children: JSON.stringify(ld) }],
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...(image
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PublicEventPage,
  codeSplitGroupings: [["component", "errorComponent", "notFoundComponent"]],
  errorComponent: EventUnavailable,
  notFoundComponent: EventUnavailable,
});

function EventUnavailable() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <MrDriveAvatar variant="home" size={96} />
      <h1 className="font-brand text-xl font-bold text-td-dark">Мероприятие недоступно</h1>
      <p className="text-sm text-td-muted">Возможно, оно ещё на модерации или ссылка устарела.</p>
      <a href="/" className="rounded-full bg-td-teal px-5 py-2.5 text-sm font-semibold text-white">
        На главную
      </a>
    </div>
  );
}

function PublicEventPage() {
  const event = Route.useLoaderData();
  const { eventId } = Route.useParams();
  const { user } = useAuth();

  // Сохраняем ?ref=<user_id> из пригласительной ссылки — начисления подключим позже.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const ref = new URL(window.location.href).searchParams.get("ref");
      if (ref) window.localStorage.setItem(INVITE_REF_KEY, ref);
    } catch {
      /* ignore */
    }
  }, []);

  // Авторизованный человек не должен видеть витрину — уводим сразу в Афишу
  // с уже открытой карточкой этого мероприятия.
  useEffect(() => {
    if (!user) return;
    window.location.replace(`/events?event=${eventId}`);
  }, [user, eventId]);

  if (!event) return <EventUnavailable />;
  if (user) return null;

  const regMode = registrationMode(event);
  const isPast = isPastEvent(event);
  const safeDescription = sanitizePublicDescription(event.description);

  const cityEvent = !event.organizer_user_id;
  const cityTicket = cityEvent ? ticketLink(event.ticket_url) : null;
  const place = placeText(event.location_details);
  const mapUrl = mapUrlFrom(event.location_details);
  const singleOrganizer = cityEvent && !!event.co_organizer_name?.trim();
  const date = event.event_date ? new Date(event.event_date) : null;
  const weekday = date?.toLocaleDateString("ru-RU", { weekday: "short", timeZone: TZ }) ?? "";
  const multiDay = !!event.event_date && !!event.event_end_date &&
    istanbulParts(event.event_date).ymd !== istanbulParts(event.event_end_date).ymd;
  const time = event.event_date ? istanbulParts(event.event_date).hm : null;

  function goToAuth(mode: "signup" | "signin") {
    const target = `/events?event=${eventId}`;
    if (!isPast && (regMode === "in_app" || (cityEvent && mode === "signup"))) setPendingRsvp(eventId);
    setRedirectIntent(target);
    window.location.assign(`/auth?redirect=${encodeURIComponent(target)}&mode=${mode}`);
  }

  return (
    <main className="min-h-screen overflow-x-hidden bg-gradient-to-b from-td-teal/10 via-white to-white pb-[calc(env(safe-area-inset-bottom)+108px)]">
      <div className="mx-auto min-w-0 w-full max-w-[520px] px-4 pt-5">
        <div className="flex items-center justify-between gap-3">
          <a href="/" className="inline-flex min-h-11 items-center text-[13px] text-td-muted hover:text-td-dark">
            ← TurkeyDrive
          </a>
          <Button variant="link" onClick={() => goToAuth("signin")} className="min-h-11 min-w-11 px-2 text-sm font-semibold text-td-teal">
            Войти
          </Button>
        </div>

        <h1 className="mt-5 break-words font-brand text-2xl font-bold leading-tight text-td-dark [overflow-wrap:anywhere]">
          {event.title}
        </h1>
        <AegisBadge eventId={event.id} className="mt-2" />
        {isPast && <p className="mt-2 text-sm font-semibold text-td-muted">Событие прошло</p>}

        <section aria-label="Главное" className="mt-4 flex min-w-0 items-start gap-3 rounded-2xl border border-td-teal/30 bg-td-teal/5 p-4">
          {date && (
            <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-lg bg-td-teal/10 text-td-dark">
              <span className="text-[10px] uppercase leading-3">{date.toLocaleDateString("ru-RU", { month: "short", timeZone: TZ })}</span>
              <span className="text-2xl font-bold leading-7">{date.toLocaleDateString("ru-RU", { day: "numeric", timeZone: TZ })}</span>
              <span className="text-[10px] uppercase leading-3">{weekday}</span>
            </div>
          )}
          <div className="min-w-0 flex-1 space-y-1 break-words [overflow-wrap:anywhere]">
            <div className="text-lg font-bold leading-snug text-td-dark">
              {event.event_date ? (
                time === "00:00" ? <span className="font-normal text-td-muted">время уточняется</span> : time
              ) : "Дата уточняется"}
            </div>
            {multiDay && event.event_end_date && (
              <div className="text-xs text-td-muted">до {dayLabel(event.event_end_date)}</div>
            )}
            {event.city && (mapUrl ? (
              <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="flex min-w-0 items-start text-base font-semibold text-td-dark hover:text-td-teal">
                <span className="line-clamp-2 min-w-0 flex-1">
                  <MapPin className="mr-1 inline h-4 w-4" aria-hidden />{event.city}{place ? ` · ${place}` : ""}
                </span>
                <span className="ml-1 shrink-0 text-td-teal" aria-hidden>→</span>
              </a>
            ) : (
              <div className="text-base font-semibold text-td-dark">
                <span className="line-clamp-2 min-w-0"><MapPin className="mr-1 inline h-4 w-4" aria-hidden />{event.city}{place ? ` · ${place}` : ""}</span>
              </div>
            ))}
          </div>
        </section>

        {event.cover_image_url && (
          <SquareCover url={event.cover_image_url} title={event.title} className="mt-4 aspect-square w-full rounded-2xl" />
        )}

        {safeDescription && (
          <p className="mt-5 whitespace-pre-line break-words text-[15px] leading-relaxed text-td-dark [overflow-wrap:anywhere]">
            {safeDescription}
          </p>
        )}

        {singleOrganizer && (
          <div className="mt-6 flex min-w-0 items-center gap-3 rounded-2xl border border-td-line bg-white p-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-td-soft text-td-muted">
              🙂
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs text-td-muted">Организатор</div>
              <div className="break-words text-sm font-semibold text-td-dark [overflow-wrap:anywhere]">
                {event.co_organizer_name}
              </div>
              <div className="mt-1 text-[13px] text-td-muted">Билеты и изменения программы смотри у организатора</div>
            </div>
          </div>
        )}

        {!singleOrganizer && event.organizer_name && (
          <div className="mt-6 flex min-w-0 items-center gap-3 rounded-2xl border border-td-line bg-white p-3">
            {event.organizer_photo_url ? (
              <img
                decoding="async"
                src={event.organizer_photo_url}
                alt={event.organizer_name}
                className="h-11 w-11 rounded-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-td-soft text-td-muted">
                🙂
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs text-td-muted">Организатор</div>
              <div className="break-words text-sm font-semibold text-td-dark [overflow-wrap:anywhere]">
                {event.organizer_name}
              </div>
            </div>
          </div>
        )}

        {!singleOrganizer && event.co_organizer_name && (
          <div className="mt-3 flex min-w-0 items-center gap-3 rounded-2xl border border-td-line bg-white p-3">
            {event.co_organizer_photo_url ? (
              <img
                decoding="async"
                src={event.co_organizer_photo_url}
                alt={event.co_organizer_name}
                className="h-11 w-11 rounded-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-td-soft text-td-muted">
                🙂
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs text-td-muted">Соорганизатор</div>
              <div className="break-words text-sm font-semibold text-td-dark [overflow-wrap:anywhere]">
                {event.co_organizer_name}
              </div>
            </div>
          </div>
        )}

        {regMode !== "in_app" && <p className="mt-4 text-[13px] text-td-muted">Запись на это событие у организатора. Контакты видны участникам сообщества.</p>}

        <GuestInviteBlock
          className="mt-6"
          onSignup={() => goToAuth("signup")}
          onLogin={() => goToAuth("signin")}
        />

      </div>

      {!isPast && <div className="fixed inset-x-0 bottom-0 z-50 border-t border-td-line bg-background/95 p-3 pb-[calc(env(safe-area-inset-bottom)+12px)] backdrop-blur">
        <div className="mx-auto max-w-[520px]">
          {cityEvent ? (
            <>
              <div className="flex gap-2">
                {cityTicket && (
                  <a
                    href={cityTicket}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-h-12 min-w-0 flex-1 items-center justify-center rounded-full bg-td-teal px-4 py-1 text-center text-base font-semibold leading-tight text-white hover:bg-td-teal/90"
                  >
                    {/[1-9]/.test(event.price ?? "") ? "Купить билет" : "Сайт организатора"}
                  </a>
                )}
                <Button
                  type="button"
                  onClick={() => goToAuth("signup")}
                  className={
                    cityTicket
                      ? "min-h-12 min-w-0 flex-1 rounded-full border border-td-teal bg-white px-4 text-base font-semibold text-td-teal hover:bg-td-teal/10"
                      : "h-12 w-full rounded-full bg-td-teal px-6 text-base font-semibold text-white hover:bg-td-teal/90"
                  }
                >
                  Я иду
                </Button>
              </div>
            </>
          ) : (
            <>
            <Button
              type="button"
              onClick={() => goToAuth("signup")}
              className={
                regMode === "in_app"
                  ? "h-12 w-full rounded-full bg-td-teal px-6 text-base font-semibold text-white hover:bg-td-teal/90"
                  : "h-12 w-full rounded-full border border-td-teal bg-white px-6 text-base font-semibold text-td-teal hover:bg-td-teal/10"
              }
            >
              {regMode === "in_app" ? "Я иду" : "Записаться у организатора"}
            </Button>
            </>
          )}
          <p className="mt-2 text-center text-[12px] leading-4 text-td-muted">Регистрация займёт минуту</p>
        </div>
      </div>}
    </main>
  );
}
